/**
 * Recommended table framing for the live camera preview.
 * Coordinates are percentages of the preview (x to the right, y downward).
 *
 * Spectator: phone at the right corner of the court, looking at the middle.
 * The frame splits in thirds — left player, table polygon, right player.
 * Referee: phone at the side, full length across the frame.
 * Scorecam: near end under a scoreboard target, or the full side view
 * when this phone also records the players.
 */

export const TABLE_GUIDE_STORAGE_KEY = "cm-table-guide";

function pt(x, y) {
  return { x, y };
}

function lerp(a, b, t) {
  return { x: a.x + (b.x - a.x) * t, y: a.y + (b.y - a.y) * t };
}

function centroid(quad) {
  return {
    x: (quad[0].x + quad[1].x + quad[2].x + quad[3].x) / 4,
    y: (quad[0].y + quad[1].y + quad[2].y + quad[3].y) / 4,
  };
}

/** Pull each corner toward the middle so the white band reads as the edge line. */
function insetQuad(quad, amount) {
  const c = centroid(quad);
  return quad.map((p) => {
    const dx = c.x - p.x;
    const dy = c.y - p.y;
    const dist = Math.hypot(dx, dy) || 1;
    const scale = Math.max(0, dist - amount) / dist;
    return { x: c.x - dx * scale, y: c.y - dy * scale };
  });
}

/** t = 0 on the far edge, t = 1 on the near edge. */
function crossSection(quad, tFromFar) {
  const [nearLeft, nearRight, farRight, farLeft] = quad;
  return {
    left: lerp(farLeft, nearLeft, tFromFar),
    right: lerp(farRight, nearRight, tFromFar),
  };
}

function cornerBrackets(quad, length) {
  const neighbors = [
    [1, 3],
    [0, 2],
    [1, 3],
    [2, 0],
  ];
  return quad.flatMap((corner, index) =>
    neighbors[index].map((otherIndex) => {
      const other = quad[otherIndex];
      const dx = other.x - corner.x;
      const dy = other.y - corner.y;
      const dist = Math.hypot(dx, dy) || 1;
      return [
        corner,
        {
          x: corner.x + (dx / dist) * length,
          y: corner.y + (dy / dist) * length,
        },
      ];
    })
  );
}

function finish(position, surface, extras) {
  return {
    position,
    surface,
    playing: insetQuad(surface, 1.7),
    ticks: cornerBrackets(surface, 4.8),
    scoreboard: null,
    ...extras,
  };
}

function endOn({
  position,
  nearLeft,
  nearRight,
  farRight,
  farLeft,
  netFromFar,
  netRise,
  hintKey,
  hint,
  scoreboard = null,
}) {
  const surface = [nearLeft, nearRight, farRight, farLeft];
  const netLine = crossSection(surface, netFromFar);
  const net = [netLine.left, netLine.right];
  const netMid = lerp(net[0], net[1], 0.5);
  const center = [
    [lerp(nearLeft, nearRight, 0.5), netMid],
    [netMid, lerp(farLeft, farRight, 0.5)],
  ].filter(([a, b]) => Math.hypot(a.x - b.x, a.y - b.y) > 0.8);
  return finish(position, surface, {
    layout: "end",
    hintKey,
    hint,
    net,
    netTop: net.map((p) => ({ x: p.x, y: p.y - netRise })),
    center,
    scoreboard,
  });
}

function sideOn({
  position,
  nearLeft,
  nearRight,
  farRight,
  farLeft,
  centerFromFar,
  netRiseFar,
  netRiseNear,
  hintKey,
  hint,
  scoreboard = null,
}) {
  const surface = [nearLeft, nearRight, farRight, farLeft];
  const farNet = lerp(farLeft, farRight, 0.5);
  const nearNet = lerp(nearLeft, nearRight, 0.5);
  const band = crossSection(surface, centerFromFar);
  const meet = lerp(farNet, nearNet, centerFromFar);
  return finish(position, surface, {
    layout: "side",
    hintKey,
    hint,
    net: [farNet, nearNet],
    netTop: [
      { x: farNet.x, y: farNet.y - netRiseFar },
      { x: nearNet.x, y: nearNet.y - netRiseNear },
    ],
    center: [
      [band.left, meet],
      [meet, band.right],
    ],
    scoreboard,
  });
}

function spectatorModel(landscape) {
  // Right-corner view aimed at the middle. The near-right corner sits lower
  // because that corner is closest to the phone. The polygon stays in the
  // center third so a player fits on each side.
  return endOn({
    position: "spectator",
    ...(landscape
      ? {
          nearLeft: pt(34, 66),
          nearRight: pt(66, 80),
          farRight: pt(60, 30),
          farLeft: pt(40, 34),
          netRise: 5.6,
        }
      : {
          nearLeft: pt(33, 64),
          nearRight: pt(67, 82),
          farRight: pt(62, 34),
          farLeft: pt(38, 42),
          netRise: 6.2,
        }),
    netFromFar: 0.5,
    hintKey: "cmTableGuideSpectator",
    hint: "Right corner. Left player, table, and right player — each a third.",
  });
}

function refereeModel(landscape) {
  return sideOn({
    position: "referee",
    ...(landscape
      ? {
          nearLeft: pt(5, 74),
          nearRight: pt(95, 74),
          farRight: pt(88, 38),
          farLeft: pt(12, 38),
          netRiseFar: 7,
          netRiseNear: 12,
        }
      : {
          nearLeft: pt(7, 73),
          nearRight: pt(93, 73),
          farRight: pt(84, 49),
          farLeft: pt(16, 49),
          netRiseFar: 6.5,
          netRiseNear: 11,
        }),
    centerFromFar: 0.38,
    hintKey: "cmTableGuideReferee",
    hint: "Line the full length up with the outline.",
  });
}

function scorecamModel(landscape) {
  return endOn({
    position: "scorecam",
    ...(landscape
      ? {
          nearLeft: pt(22, 88),
          nearRight: pt(78, 88),
          farRight: pt(64, 48),
          farLeft: pt(36, 48),
          scoreboard: { x: 28, y: 16, w: 44, h: 16 },
        }
      : {
          nearLeft: pt(10, 90),
          nearRight: pt(90, 90),
          farRight: pt(72, 58),
          farLeft: pt(28, 58),
          scoreboard: { x: 17, y: 20, w: 66, h: 22 },
        }),
    netFromFar: 0,
    netRise: 4.2,
    hintKey: "cmTableGuideScorecam",
    hint: "Near end on the outline, scoreboard in the box.",
  });
}

function scorecamPlayersModel(landscape) {
  return sideOn({
    position: "scorecam",
    ...(landscape
      ? {
          nearLeft: pt(8, 78),
          nearRight: pt(94, 78),
          farRight: pt(86, 42),
          farLeft: pt(14, 42),
          scoreboard: { x: 3, y: 18, w: 22, h: 12 },
        }
      : {
          nearLeft: pt(8, 80),
          nearRight: pt(92, 80),
          farRight: pt(83, 52),
          farLeft: pt(17, 52),
          scoreboard: { x: 4, y: 20, w: 28, h: 13 },
        }),
    centerFromFar: 0.38,
    netRiseFar: 5.5,
    netRiseNear: 9.5,
    hintKey: "cmTableGuideScorecamVideo",
    hint: "Full table on the outline, scoreboard in the box.",
  });
}

export function normalizeTableGuidePosition(value) {
  const raw = String(value || "")
    .trim()
    .toLowerCase();
  if (raw === "referee" || raw === "side" || raw === "long") return "referee";
  if (raw === "scorecam" || raw === "score" || raw === "score_camera") {
    return "scorecam";
  }
  return "spectator";
}

export function tableCameraGuideModel(position, options = {}) {
  const pos = normalizeTableGuidePosition(position);
  const landscape = options.landscape === true;
  if (pos === "referee") return refereeModel(landscape);
  if (pos === "scorecam") {
    return options.players === true
      ? scorecamPlayersModel(landscape)
      : scorecamModel(landscape);
  }
  return spectatorModel(landscape);
}

export function readTableGuideVisible(storage) {
  const store = storage || (typeof localStorage !== "undefined" ? localStorage : null);
  try {
    return store?.getItem(TABLE_GUIDE_STORAGE_KEY) !== "0";
  } catch {
    return true;
  }
}

export function writeTableGuideVisible(visible, storage) {
  const store = storage || (typeof localStorage !== "undefined" ? localStorage : null);
  try {
    store?.setItem(TABLE_GUIDE_STORAGE_KEY, visible ? "1" : "0");
  } catch {
    /* ignore */
  }
}
